import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { recoverFromStaleRelease } from '@/lib/release-recovery';
import { useSiteContext } from '@/composables/useSiteContext';

const routes = [
  { path: '/', component: () => import('@/views/public/HomeView.vue'), meta: { public: true, title: 'Pivot Logistics ERP' } },
  { path: '/about', component: () => import('@/views/public/AboutView.vue'), meta: { public: true, title: 'About Pivot Logistics ERP' } },
  { path: '/services', component: () => import('@/views/public/ServicesView.vue'), meta: { public: true, title: 'Logistics Services' } },
  { path: '/locations', component: () => import('@/views/public/LocationsView.vue'), meta: { public: true, title: 'Locations' } },
  { path: '/contact', component: () => import('@/views/public/ContactView.vue'), meta: { public: true, title: 'Contact' } },
  { path: '/quote', component: () => import('@/views/public/QuoteView.vue'), meta: { public: true, title: 'Request a Quote' } },
  { path: '/track', component: () => import('@/views/public/TrackView.vue'), meta: { public: true, essentialAllowed: true, title: 'Track Cargo' } },
  { path: '/tracking/:token', component: () => import('@/views/public/TrackingLinkView.vue'), meta: { public: true, essentialAllowed: true, title: 'Cargo Tracking' } },
  { path: '/pricing', component: () => import('@/views/public/PricingView.vue'), meta: { public: true, saasOnly: true, title: 'Pricing' } },
  { path: '/create-workspace', component: () => import('@/views/public/CreateWorkspaceView.vue'), meta: { public: true, saasOnly: true, title: 'Create Workspace' } },
  { path: '/portal', component: () => import('@/views/public/CustomerPortalView.vue'), meta: { public: true, workspaceOnly: true, title: 'Customer Portal' } },
  { path: '/shared-document/:token', component: () => import('@/views/public/SharedDocumentView.vue'), meta: { public: true, title: 'Shared Document' } },
  { path: '/login', component: () => import('@/views/LoginView.vue'), meta: { public: true, essentialAllowed: true, title: 'Workspace Login' } },
  { path: '/driver/login', component: () => import('@/views/DriverLoginView.vue'), meta: { public: true, essentialAllowed: true, title: 'Driver Login' } },
  { path: '/saas-admin/login', component: () => import('@/views/SaaSAdminLoginView.vue'), meta: { public: true, saasOnly: true, title: 'SaaS Administrator Login' } },
  { path: '/saas-admin/forgot-password', component: () => import('@/views/SaaSForgotPasswordView.vue'), meta: { public: true, saasOnly: true, title: 'Recover SaaS Administrator Access' } },
  { path: '/saas-admin/reset-password', component: () => import('@/views/SaaSResetPasswordView.vue'), meta: { public: true, saasOnly: true, title: 'Reset SaaS Administrator Password' } },
  { path: '/partner', component: () => import('@/views/PartnerPortalView.vue'), meta: { partner: true, title: 'Partner Portal' } },
  { path: '/driver', component: () => import('@/views/PartnerPortalView.vue'), meta: { driver: true, title: 'Driver Portal' } },
  {
    path: '/platform',
    component: () => import('@/layouts/PlatformShell.vue'),
    meta: { platformAdmin: true, title: 'SaaS Management' },
    children: [
      { path: '', name: 'platform', component: () => import('@/views/PlatformAdminView.vue'), meta: { platformAdmin: true, title: 'SaaS Management' } }
    ]
  },
  {
    path: '/erp', component: () => import('@/layouts/AppShell.vue'), children: [
      { path: '', redirect: '/erp/dashboard' },
      { path: 'dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
      { path: 'crm', name: 'crm', component: () => import('@/views/CrmView.vue') },
      { path: 'sales-agents', name: 'sales-agents', component: () => import('@/views/SalesCommissionsView.vue') },
      { path: 'customers', name: 'customers', component: () => import('@/views/RegistryView.vue'), meta: { module: 'customers' } },
      { path: 'customer-cargo-consolidation', name: 'customer-cargo-consolidation', component: () => import('@/views/CustomerConsolidationView.vue') },
      { path: 'suppliers', name: 'suppliers', component: () => import('@/views/RegistryView.vue'), meta: { module: 'suppliers' } },
      { path: 'warehouse/receipts', name: 'warehouse', component: () => import('@/views/WarehouseView.vue') },
      { path: 'warehouse/ready-loading', name: 'ready-loading', component: () => import('@/views/ReadyLoadingView.vue') },
      { path: 'loading-plans', name: 'loading-plans', component: () => import('@/views/LoadingPlanView.vue') },
      { path: 'shipping-instructions', name: 'shipping-instructions', component: () => import('@/views/ShippingInstructionView.vue') },
      { path: 'packing-lists', name: 'packing-lists', component: () => import('@/views/PackingListsView.vue') },
      { path: 'shipments', name: 'shipments', component: () => import('@/views/OperationsView.vue'), meta: { module: 'shipments' } },
      { path: 'containers', name: 'containers', component: () => import('@/views/OperationsView.vue'), meta: { module: 'containers' } },
      { path: 'container-movement', name: 'container-movement', component: () => import('@/views/ContainerMovementView.vue') },
      { path: 'clearance', name: 'clearance', component: () => import('@/views/OperationsView.vue'), meta: { module: 'clearance' } },
      { path: 'transport', name: 'transport', component: () => import('@/views/OperationsView.vue'), meta: { module: 'transport' } },
      { path: 'bonded-warehouse', name: 'bonded-warehouse', component: () => import('@/views/BondedWarehouseView.vue') },
      { path: 'container-tracking', name: 'container-tracking', component: () => import('@/views/ContainerTrackingView.vue') },
      { path: 'container-returns', name: 'container-returns', component: () => import('@/views/OperationsView.vue'), meta: { module: 'returns' } },
      { path: 'invoices', name: 'invoices', component: () => import('@/views/FinanceListView.vue'), meta: { module: 'invoices' } },
      { path: 'collections', name: 'collections', component: () => import('@/views/FinanceListView.vue'), meta: { module: 'collections' } },
      { path: 'expenses', name: 'expenses', component: () => import('@/views/FinanceListView.vue'), meta: { module: 'expenses' } },
      { path: 'container-costs', name: 'container-costs', component: () => import('@/views/FinanceListView.vue'), meta: { module: 'containerCosts' } },
      { path: 'accounting', name: 'accounting', component: () => import('@/views/AccountingView.vue') },
      { path: 'statements', name: 'statements', component: () => import('@/views/StatementsView.vue') },
      { path: 'hr', name: 'hr', component: () => import('@/views/HumanResourcesView.vue') },
      { path: 'claims', name: 'claims', component: () => import('@/views/ClaimsView.vue') },
      { path: 'procurement', name: 'procurement', component: () => import('@/views/RegistryView.vue'), meta: { module: 'procurement' } },
      { path: 'assets', name: 'assets', component: () => import('@/views/RegistryView.vue'), meta: { module: 'assets' } },
      { path: 'reports', name: 'reports', component: () => import('@/views/ReportsView.vue') },
      { path: 'branch-control', name: 'branch-control', component: () => import('@/views/BranchControlView.vue') },
      { path: 'operational-setup', name: 'operational-setup', component: () => import('@/views/OperationalSetupView.vue') },
      { path: 'partner-access', name: 'partner-access', component: () => import('@/views/PartnerAccessView.vue') },
      { path: 'subscription', name: 'subscription', component: () => import('@/views/SubscriptionView.vue') },
      { path: 'platform', redirect: '/platform' },
      { path: 'settings', name: 'settings', component: () => import('@/views/SettingsView.vue') }
    ]
  },
  { path: '/:pathMatch(.*)*', redirect: '/' }
];

const router = createRouter({ history: createWebHistory(), routes });

type SessionExpiredDetail = { loginContext?: 'workspace' | 'platform' | 'driver'; redirect?: string };

function resetGlobalLoader(): void {
  window.dispatchEvent(new CustomEvent('pivot:loading-reset'));
}

window.addEventListener('pivot:session-expired', (event: Event) => {
  const detail = (event as CustomEvent<SessionExpiredDetail>).detail ?? {};
  const auth = useAuthStore();
  const loginContext = detail.loginContext === 'platform' ? 'platform' : detail.loginContext === 'driver' ? 'driver' : 'workspace';
  const target = loginContext === 'platform' ? '/saas-admin/login' : loginContext === 'driver' ? '/driver/login' : '/login';
  const redirect = detail.redirect ?? router.currentRoute.value.fullPath;
  auth.logout();
  resetGlobalLoader();
  if (router.currentRoute.value.path === target) return;
  void router.replace({ path: target, query: { reason: 'session-expired', ...(redirect && redirect !== target ? { redirect } : {}) } })
    .finally(resetGlobalLoader);
});

window.addEventListener('pivot:subscription-expired', (event: Event) => {
  const detail = (event as CustomEvent<SessionExpiredDetail>).detail ?? {};
  const auth = useAuthStore();
  const redirect = detail.redirect ?? router.currentRoute.value.fullPath;
  const target = detail.loginContext === 'driver' ? '/driver/login' : '/login';
  auth.logout();
  resetGlobalLoader();
  if (router.currentRoute.value.path === target) return;
  void router.replace({ path: target, query: { reason: 'subscription-expired', ...(redirect && redirect !== target ? { redirect } : {}) } })
    .finally(resetGlobalLoader);
});

router.beforeEach(async (to) => {
  window.dispatchEvent(new CustomEvent('pivot:loading-start'));
  const auth = useAuthStore();
  const platformRoute = Boolean(to.meta.platformAdmin);

  if (to.meta.public) {
    const site = useSiteContext();
    await site.loadSiteContext();
    if (to.meta.saasOnly && site.isWorkspaceSite.value) return site.essentialAccessOnly.value ? '/track' : '/';
    if (to.meta.workspaceOnly && !site.isWorkspaceSite.value) return '/track';
    if (site.essentialAccessOnly.value && !to.meta.essentialAllowed) return { path:'/track', query:{ website:'essential-access' } };
  }

  if (platformRoute) {
    const valid = await auth.verify();
    if (!valid) return { path: '/saas-admin/login', query: { redirect: to.fullPath, reason: 'session-expired' } };
    if (!auth.isPlatformAdminSession) {
      return { path: '/saas-admin/login', query: { redirect: to.fullPath, reason: 'platform-auth-required' } };
    }
    return true;
  }

  if (!to.meta.public) {
    const valid = await auth.verify();
    const loginTarget = to.meta.driver ? '/driver/login' : '/login';
    if (!valid) return { path: loginTarget, query: { redirect: to.fullPath, reason: 'session-expired' } };
    if (auth.loginContext === 'platform') return '/platform';
    if (auth.loginContext === 'driver') {
      if (!auth.user?.roles.includes('DRIVER')) return { path: '/driver/login', query: { reason: 'driver-auth-required' } };
      if (!to.meta.driver) return '/driver';
      return true;
    }
    if (to.meta.driver) return { path: '/driver/login', query: { redirect: to.fullPath, reason: 'driver-auth-required' } };
    const partner = auth.user?.roles.some((role) => ['CLEARING_AGENT','TRANSPORTER'].includes(role));
    if (to.meta.partner && !partner) return '/erp/dashboard';
    if (to.path.startsWith('/erp') && partner) return '/partner';
  }

  if (to.path === '/login' && auth.authenticated && await auth.verify()) {
    if (auth.loginContext === 'platform') return '/platform';
    if (auth.loginContext === 'driver') return '/driver';
    return auth.user?.roles.some((role) => ['CLEARING_AGENT','TRANSPORTER'].includes(role)) ? '/partner' : '/erp/dashboard';
  }

  if (to.path === '/driver/login' && auth.authenticated && await auth.verify() && auth.loginContext === 'driver') {
    return '/driver';
  }

  if (to.path === '/saas-admin/login' && auth.authenticated && await auth.verify() && auth.isPlatformAdminSession) {
    return '/platform';
  }

  return true;
});

router.afterEach((to) => {
  window.dispatchEvent(new CustomEvent('pivot:loading-end'));
  const title = typeof to.meta.title === 'string' ? to.meta.title : 'Pivot Logistics ERP';
  const site = useSiteContext();
  document.title = `${title} · ${site.context.value.companyName || 'Pivot Logistics'}`;
});

router.onError((error, to) => {
  resetGlobalLoader();
  recoverFromStaleRelease(error, to?.fullPath);
});

export default router;
